import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LogOut, CheckCircle2, Circle, ArrowRight, Calendar,
  FileText, Shield, BookOpen, Target, Briefcase, Star, ExternalLink,
  ClipboardList, CreditCard, PenTool, Rocket, Upload, Loader2,
  UserCheck, AlertTriangle, Clock, Trophy, Flame, Zap, Award, Crown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { RTBLogo } from '@/components/RTBLogo';
import { LegalFooter } from '@/components/LegalFooter';
import {
  getMyProfile, getPublicSettings, getQuestionnaire, submitQuestionnaire,
  confirmPayment, signContract, completeOnboarding, completeDay,
  uploadIdDocument, getPaymentStatus, getMyBackgroundCheck, initiateBackgroundCheck
} from '@/lib/api';

const ONBOARDING_STEPS = [
  { id: 0, label: "Questionnaire", icon: ClipboardList, desc: "Complete your business personality assessment" },
  { id: 1, label: "Payment", icon: CreditCard, desc: "Complete your $75 enrollment" },
  { id: 2, label: "Contract", icon: PenTool, desc: "Sign your mentorship agreement" },
  { id: 3, label: "Welcome", icon: Rocket, desc: "Access your welcome materials" },
];

const PROGRAM_DAYS = [
  { day: 1, title: "Entrepreneurial Mindset", phase: "foundation", desc: "Set your vision, define your why, and commit to the journey." },
  { day: 2, title: "Choose Your Business Path", phase: "foundation", desc: "Review your questionnaire results and select your business direction." },
  { day: 3, title: "LLC Formation Begins", phase: "foundation", desc: "Start your LLC formation process with our step-by-step guide." },
  { day: 4, title: "Business Identity", phase: "foundation", desc: "Name, branding basics, and establishing your professional presence." },
  { day: 5, title: "EIN & Bank Account", phase: "foundation", desc: "Get your EIN and open your business bank account." },
  { day: 6, title: "Financial Literacy Foundations", phase: "structure", desc: "Tax strategies and financial optimization for entrepreneurs." },
  { day: 7, title: "Credit Building 101", phase: "structure", desc: "Strategies to reach 670+ credit score in 45 days." },
  { day: 8, title: "Business Plan Basics", phase: "structure", desc: "Create your lean business plan and revenue model." },
  { day: 9, title: "Funding Strategies", phase: "structure", desc: "Access up to $250,000 in business funding." },
  { day: 10, title: "Legal Foundations", phase: "structure", desc: "Contracts, insurance, and protecting your business." },
  { day: 11, title: "Marketing Fundamentals", phase: "activation", desc: "Social media, branding, and your first marketing campaign." },
  { day: 12, title: "Client Acquisition", phase: "activation", desc: "Finding and converting your first clients." },
  { day: 13, title: "Revenue Launch", phase: "activation", desc: "Set up payment systems and make your first sale." },
  { day: 14, title: "Networking & Partnerships", phase: "activation", desc: "Build relationships that grow your business." },
  { day: 15, title: "Systems & Automation", phase: "activation", desc: "Tools and processes to run efficiently." },
  { day: 16, title: "Growth Strategy", phase: "expansion", desc: "Scaling beyond your first clients." },
  { day: 17, title: "Team Building", phase: "expansion", desc: "When and how to bring on help." },
  { day: 18, title: "Advanced Revenue", phase: "expansion", desc: "Multiple income streams and recurring revenue." },
  { day: 19, title: "Legacy Planning", phase: "expansion", desc: "Generational wealth and long-term vision." },
  { day: 20, title: "Graduation & Next Steps", phase: "expansion", desc: "Celebrate and plan your Mastermind journey." },
];

const PHASE_COLORS = {
  foundation: "text-rtb-purple",
  structure: "text-rtb-purple-light",
  activation: "text-rtb-green",
  expansion: "text-emerald-400",
};

const PHASE_BG = {
  foundation: "bg-rtb-purple/10",
  structure: "bg-rtb-purple-light/10",
  activation: "bg-rtb-green/10",
  expansion: "bg-emerald-400/10",
};

const PHASE_ICONS = { foundation: Target, structure: BookOpen, activation: Briefcase, expansion: Star };

const MILESTONES = [
  { day: 1, label: "First Step", icon: Rocket, color: "text-blue-500", bg: "bg-blue-100", desc: "You started the journey!" },
  { day: 3, label: "LLC Initiated", icon: Shield, color: "text-rtb-purple", bg: "bg-rtb-purple/10", desc: "LLC formation began" },
  { day: 5, label: "Week 1 Complete", icon: Flame, color: "text-orange-500", bg: "bg-orange-100", desc: "First week down!" },
  { day: 10, label: "Halfway Hero", icon: Trophy, color: "text-amber-500", bg: "bg-amber-100", desc: "You're halfway there!" },
  { day: 15, label: "Activator", icon: Zap, color: "text-rtb-green", bg: "bg-rtb-green/10", desc: "Business is live!" },
  { day: 20, label: "Graduate", icon: Crown, color: "text-yellow-500", bg: "bg-yellow-100", desc: "Program complete!" },
];

const fadeIn = { hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

function MeetingsTab({ settings }) {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const API = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('rtb_user_token');

  useEffect(() => {
    fetch(`${API}/api/participant/meetings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setMeetings(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [API, token]);

  if (loading) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-rtb-purple border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="meetings-tab">
      {settings.calendly_mastery_link && (
        <a href={settings.calendly_mastery_link} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-3 p-4 bg-white border border-gray-100 hover:border-rtb-purple/30 hover:shadow-md transition-all rounded-xl">
          <Calendar className="w-5 h-5 text-rtb-purple" />
          <div className="flex-1">
            <p className="font-medium text-gray-900 text-sm">Book a Mastery Session</p>
            <p className="text-gray-400 text-xs">Wednesday & Saturday - 40 min</p>
          </div>
          <ExternalLink className="w-4 h-4 text-gray-300" />
        </a>
      )}
      {meetings.length === 0 && (
        <div className="text-center py-10">
          <Calendar className="w-10 h-10 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">No upcoming meetings scheduled.</p>
          <p className="text-gray-300 text-xs">Check back soon — your mentor will post sessions here.</p>
        </div>
      )}
      {meetings.map(m => (
        <div key={m.id} className="p-4 bg-white border border-gray-100 rounded-xl hover:border-rtb-purple/20 transition-all" data-testid={`meeting-card-${m.id}`}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 bg-rtb-purple/10 rounded-lg flex items-center justify-center">
              <Target className="w-4 h-4 text-rtb-purple" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-gray-900">{m.title}</p>
              {m.description && <p className="text-gray-400 text-xs">{m.description}</p>}
            </div>
          </div>
          {m.scheduled_at && (
            <p className="text-gray-400 text-xs mb-3 flex items-center gap-1">
              <Calendar className="w-3 h-3" /> {new Date(m.scheduled_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
            </p>
          )}
          {m.enable_breakout && m.breakout_rooms?.length > 0 && (
            <div className="mb-3">
              <p className="text-gray-400 text-[10px] uppercase tracking-wide font-bold mb-1">Breakout Rooms</p>
              <div className="flex flex-wrap gap-1.5">
                {m.breakout_rooms.map((room, i) => (
                  <a key={i} href={`${m.join_url}-${room.toLowerCase().replace(/\s+/g, '-')}`} target="_blank" rel="noopener noreferrer"
                    className="px-2.5 py-1 text-xs bg-rtb-purple/5 text-rtb-purple border border-rtb-purple/10 rounded-full hover:bg-rtb-purple/10 transition-colors">
                    {room}
                  </a>
                ))}
              </div>
            </div>
          )}
          <a href={m.join_url} target="_blank" rel="noopener noreferrer">
            <Button size="sm" className="bg-rtb-purple text-white rounded-full text-xs px-6 hover:bg-rtb-purple/90 w-full sm:w-auto" data-testid={`join-${m.id}`}>
              Join Meeting <ExternalLink className="ml-1.5 w-3.5 h-3.5" />
            </Button>
          </a>
        </div>
      ))}
    </div>
  );
}

export default function Portal() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('program');
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [signature, setSignature] = useState('');
  const [idFile, setIdFile] = useState(null);
  const [uploadingId, setUploadingId] = useState(false);
  const [paymentCode, setPaymentCode] = useState('');
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [bgCheck, setBgCheck] = useState(null);

  const fetchProfile = useCallback(async () => {
    try {
      const [profile, settingsData] = await Promise.all([getMyProfile(), getPublicSettings()]);
      setUser(profile);
      setSettings(settingsData);
      if (profile.onboarding_step < 4) setActiveTab('onboarding');
      else setActiveTab('program');
      // Fetch background check status
      try {
        const check = await getMyBackgroundCheck();
        setBgCheck(check);
      } catch {}
    } catch {
      localStorage.removeItem('rtb_user_token');
      navigate('/login');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem('rtb_user_token')) { navigate('/login'); return; }
    fetchProfile();
  }, [navigate, fetchProfile]);

  // Poll Stripe payment status if returning from checkout
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const payment = searchParams.get('payment');
    if (sessionId && payment === 'success') {
      setCheckingPayment(true);
      const poll = async (attempts = 0) => {
        if (attempts >= 5) { setCheckingPayment(false); return; }
        try {
          const status = await getPaymentStatus(sessionId);
          if (status.payment_status === 'paid') {
            toast.success('Mastermind upgrade confirmed!');
            setCheckingPayment(false);
            fetchProfile();
            navigate('/portal', { replace: true });
            return;
          }
        } catch {}
        setTimeout(() => poll(attempts + 1), 2000);
      };
      poll();
    }
    if (payment === 'cancelled') {
      toast.info('Payment cancelled');
      navigate('/portal', { replace: true });
    }
  }, [searchParams, fetchProfile, navigate]);

  useEffect(() => {
    if (activeTab === 'onboarding' && user?.onboarding_step === 0) {
      getQuestionnaire().then(d => setQuestions(d.questions)).catch(() => {});
    }
  }, [activeTab, user?.onboarding_step]);

  const currentDayRef = useCallback((node) => {
    if (node) setTimeout(() => node.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
  }, []);

  const handleLogout = () => { localStorage.removeItem('rtb_user_token'); navigate('/'); };

  const handleQuestionnaireSubmit = async () => {
    const unanswered = questions.filter(q => !answers[q.id]);
    if (unanswered.length > 0) { toast.error(`Please answer all questions (${unanswered.length} remaining)`); return; }
    try {
      const result = await submitQuestionnaire(answers);
      toast.success(`Great! Your recommended path: ${result.recommended_path.replace('_', ' ').toUpperCase()}`);
      fetchProfile();
    } catch { toast.error('Failed to submit questionnaire'); }
  };

  const handleConfirmPayment = async () => {
    if (!paymentCode.trim()) { toast.error('Please enter your Whop confirmation code'); return; }
    try { await confirmPayment(paymentCode); toast.success('Payment confirmed!'); fetchProfile(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Invalid confirmation code'); }
  };

  const handleIdUpload = async () => {
    if (!idFile) { toast.error('Please select a file'); return; }
    setUploadingId(true);
    try { await uploadIdDocument(idFile); toast.success('ID uploaded successfully'); setIdFile(null); }
    catch { toast.error('Failed to upload ID'); }
    finally { setUploadingId(false); }
  };

  const handleSignContract = async () => {
    if (!signature.trim()) { toast.error('Please type your full name as signature'); return; }
    try { await signContract(signature); toast.success('Contract signed!'); fetchProfile(); }
    catch { toast.error('Failed to sign contract'); }
  };

  const handleCompleteOnboarding = async () => {
    try { await completeOnboarding(); toast.success('Welcome to the program!'); fetchProfile(); }
    catch { toast.error('Failed to complete onboarding'); }
  };

  const handleCompleteDay = async (day) => {
    try { await completeDay(day); toast.success(`Day ${day} completed!`); fetchProfile(); }
    catch { toast.error('Failed to mark day complete'); }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-rtb-purple border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const isOnboarding = user?.onboarding_step < 4;
  const completedDays = user?.daily_tasks_completed || [];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="participant-portal">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 px-6 py-4 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <RTBLogo />
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-gray-400 text-sm">Hi, {user?.first_name}</span>
            <button onClick={handleLogout} className="text-gray-400 hover:text-gray-600 transition-colors" data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto flex gap-1 px-6">
          {[
            { id: 'program', label: 'Program', show: !isOnboarding },
            { id: 'onboarding', label: isOnboarding ? 'Get Started' : 'Onboarding', show: true },
            { id: 'meetings', label: 'Meetings', show: !isOnboarding },
            { id: 'llc', label: 'LLC Setup', show: !isOnboarding },
            { id: 'resources', label: 'Resources', show: !isOnboarding },
          ].filter(t => t.show).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3.5 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-rtb-purple text-rtb-purple'
                  : 'border-transparent text-gray-400 hover:text-gray-600'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Checking payment overlay */}
      {checkingPayment && (
        <div className="fixed inset-0 z-50 bg-white/80 backdrop-blur-sm flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-rtb-purple animate-spin mx-auto mb-4" />
            <p className="font-oswald text-lg font-bold text-gray-900">Confirming your payment...</p>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-6 py-8">
        <AnimatePresence mode="wait">
          {/* ONBOARDING TAB */}
          {activeTab === 'onboarding' && (
            <motion.div key="onboarding" initial="hidden" animate="visible" exit="hidden" variants={fadeIn}>
              {/* Progress */}
              <div className="flex items-center gap-2 mb-8">
                {ONBOARDING_STEPS.map((step, i) => (
                  <div key={step.id} className="flex items-center gap-2 flex-1">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                      user?.onboarding_step > step.id ? 'bg-rtb-green text-white' :
                      user?.onboarding_step === step.id ? 'bg-rtb-purple/10 text-rtb-purple border-2 border-rtb-purple' :
                      'bg-gray-100 text-gray-400'
                    }`}>
                      {user?.onboarding_step > step.id ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                    </div>
                    <span className={`text-xs hidden sm:block font-medium ${user?.onboarding_step >= step.id ? 'text-gray-700' : 'text-gray-400'}`}>
                      {step.label}
                    </span>
                    {i < ONBOARDING_STEPS.length - 1 && <div className={`flex-1 h-px ${user?.onboarding_step > step.id ? 'bg-rtb-green' : 'bg-gray-200'}`} />}
                  </div>
                ))}
              </div>

              {/* Step 0: Questionnaire */}
              {user?.onboarding_step === 0 && (
                <div data-testid="questionnaire-step">
                  <h2 className="text-2xl font-oswald font-bold text-gray-900 mb-2">Business Development Questionnaire</h2>
                  <p className="text-gray-500 mb-8">Answer these questions so we can match you with the ideal business path.</p>
                  <div className="space-y-4">
                    {questions.map((q, qi) => (
                      <motion.div key={q.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: qi * 0.05 }}
                        className="p-5 bg-white border border-gray-100 rounded-xl shadow-sm">
                        <p className="font-medium text-gray-900 mb-3"><span className="text-rtb-purple mr-2">{qi + 1}.</span>{q.question}</p>
                        {q.type === 'text' ? (
                          <input value={answers[q.id] || ''} onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                            className="w-full h-11 bg-gray-50 border border-gray-200 text-gray-900 px-4 rounded-lg focus:border-rtb-purple focus:outline-none"
                            data-testid={`q-${q.id}`} />
                        ) : (
                          <div className="grid sm:grid-cols-2 gap-2">
                            {q.options.map((opt) => (
                              <button key={opt} onClick={() => setAnswers({ ...answers, [q.id]: opt })}
                                className={`text-left p-3 border text-sm transition-all rounded-lg ${
                                  answers[q.id] === opt
                                    ? 'border-rtb-purple bg-rtb-purple/5 text-rtb-purple font-medium'
                                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                                }`}
                                data-testid={`q-${q.id}-opt`}>{opt}</button>
                            ))}
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                  <Button onClick={handleQuestionnaireSubmit} className="btn-primary mt-6" data-testid="submit-questionnaire-btn">
                    Submit Questionnaire <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              )}

              {/* Step 1: Payment */}
              {user?.onboarding_step === 1 && (
                <div className="max-w-lg mx-auto text-center" data-testid="payment-step">
                  <CreditCard className="w-14 h-14 text-rtb-purple mx-auto mb-6" />
                  <h2 className="text-2xl font-oswald font-bold text-gray-900 mb-2">Complete Your Enrollment</h2>
                  <p className="text-gray-500 mb-2">Your recommended path: <span className="text-rtb-purple font-bold uppercase">{user?.recommended_path?.replace('_', ' ')}</span></p>
                  <p className="text-gray-400 mb-8">Click below to complete your $75 payment and unlock the full program.</p>
                  <div className="space-y-4">
                    <a href={settings.whop_link || "https://whop.com/ready-to-build-generational-wealth/"} target="_blank" rel="noopener noreferrer">
                      <Button className="btn-primary w-full text-lg py-6" data-testid="pay-whop-btn">
                        Pay $75 via Whop <ExternalLink className="ml-2 w-5 h-5" />
                      </Button>
                    </a>
                    <p className="text-gray-400 text-xs">After completing payment on Whop, enter your license key below:</p>
                    <input
                      value={paymentCode}
                      onChange={(e) => setPaymentCode(e.target.value)}
                      placeholder="Enter your Whop license key (e.g. R-XXXXX-XXXXXXXX-XXXXXXW)"
                      className="w-full h-12 bg-gray-50 border border-gray-200 text-gray-900 px-4 rounded-xl text-center tracking-wider focus:border-rtb-purple focus:outline-none"
                      data-testid="payment-code-input"
                    />
                    <Button onClick={handleConfirmPayment} variant="outline" className="w-full border-rtb-purple/30 text-rtb-purple hover:bg-rtb-purple/5 rounded-full" data-testid="confirm-payment-btn">
                      Verify Payment
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 2: Contract + ID Upload */}
              {user?.onboarding_step === 2 && (
                <div data-testid="contract-step">
                  <h2 className="text-2xl font-oswald font-bold text-gray-900 mb-2">Mentorship Agreement</h2>
                  <p className="text-gray-500 mb-6">Please review and sign the mentorship contract.</p>
                  <div className="p-6 bg-white border border-gray-100 rounded-xl mb-6 max-h-80 overflow-y-auto text-sm text-gray-600 space-y-4 shadow-sm">
                    <h3 className="text-rtb-purple font-bold text-base">ReadyToBuildIt.life Mentorship Contract</h3>
                    <p>This Mentorship Agreement ("Agreement") is entered into between ReadyToBuildIt.life ("Mentor/Company") and the undersigned participant ("Mentee").</p>
                    <p><strong className="text-gray-900">1. Purpose:</strong> The Mentor agrees to provide a structured 20-day entrepreneurship mentorship program including business development guidance, LLC formation assistance, and financial literacy training.</p>
                    <p><strong className="text-gray-900">2. Program Fee:</strong> The Mentee agrees to pay a one-time fee of $75 for the mentorship program.</p>
                    <p><strong className="text-gray-900">3. Commitment:</strong> The Mentee agrees to actively participate in the program, complete daily assignments, and attend scheduled sessions.</p>
                    <p><strong className="text-gray-900">4. Confidentiality:</strong> Both parties agree to maintain confidentiality of proprietary information, business strategies, and personal details shared during the program.</p>
                    <p><strong className="text-gray-900">5. No Guarantee:</strong> While the program provides tools and guidance, results depend on individual effort. No specific income or outcome is guaranteed.</p>
                    <p><strong className="text-gray-900">6. Intellectual Property:</strong> All program materials remain the property of ReadyToBuildIt.life and may not be reproduced or distributed.</p>
                    <p><strong className="text-gray-900">7. Communication:</strong> The Mentee agrees to maintain open communication and attend scheduled meetings. Mastery sessions are held on Wednesdays and Saturdays.</p>
                    <p><strong className="text-gray-900">8. Termination:</strong> Either party may terminate this agreement with written notice. No refunds will be issued for completed portions of the program.</p>
                  </div>

                  {/* ID Upload */}
                  <div className="p-5 bg-gray-50 border border-gray-200 rounded-xl mb-6">
                    <div className="flex items-center gap-3 mb-3">
                      <Upload className="w-5 h-5 text-rtb-purple" />
                      <span className="font-semibold text-gray-900 text-sm">Upload Government ID (Optional)</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <input type="file" accept="image/*,.pdf" onChange={(e) => setIdFile(e.target.files[0])}
                        className="flex-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-rtb-purple/10 file:text-rtb-purple hover:file:bg-rtb-purple/20"
                        data-testid="id-upload-input" />
                      <Button onClick={handleIdUpload} disabled={!idFile || uploadingId} size="sm" className="bg-rtb-purple text-white rounded-full text-xs px-4" data-testid="id-upload-btn">
                        {uploadingId ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Upload'}
                      </Button>
                    </div>
                    {user?.id_document_filename && <p className="text-rtb-green text-xs mt-2 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Uploaded: {user.id_document_filename}</p>}
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="text-gray-500 text-sm block mb-2">Type your full name as electronic signature:</label>
                      <input value={signature} onChange={(e) => setSignature(e.target.value)}
                        placeholder="Your Full Legal Name"
                        className="w-full h-14 bg-white border border-gray-200 text-gray-900 px-4 font-oswald text-xl italic rounded-xl focus:border-rtb-purple focus:outline-none"
                        data-testid="contract-signature" />
                    </div>
                    <Button onClick={handleSignContract} className="btn-primary" data-testid="sign-contract-btn">
                      <PenTool className="mr-2 w-5 h-5" /> Sign Contract
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 3: Welcome */}
              {user?.onboarding_step === 3 && (
                <div className="max-w-lg mx-auto text-center" data-testid="welcome-step">
                  <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 0.6 }}>
                    <Rocket className="w-16 h-16 text-rtb-purple mx-auto mb-6" />
                  </motion.div>
                  <h2 className="text-3xl font-oswald font-bold text-gray-900 mb-4">
                    Welcome, <span className="text-rtb-purple">{user?.first_name}!</span>
                  </h2>
                  <p className="text-gray-500 mb-8">You're all set! Here are your welcome materials before we begin your 20-day journey.</p>
                  <div className="space-y-3 text-left mb-8">
                    <a href="https://www.youtube.com/watch?v=Z4gwVtYD3zA&t=49s" target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 p-4 bg-white border border-gray-100 hover:border-rtb-purple/30 hover:shadow-md transition-all rounded-xl">
                      <BookOpen className="w-5 h-5 text-rtb-purple" />
                      <span className="text-gray-700">Financial Literacy & Tax Strategy Video</span>
                      <ExternalLink className="w-4 h-4 text-gray-300 ml-auto" />
                    </a>
                    <a href="#" className="flex items-center gap-3 p-4 bg-white border border-gray-100 hover:border-rtb-purple/30 hover:shadow-md transition-all rounded-xl">
                      <FileText className="w-5 h-5 text-rtb-purple" />
                      <span className="text-gray-700">Business Development Ebook</span>
                      <ExternalLink className="w-4 h-4 text-gray-300 ml-auto" />
                    </a>
                    {settings.calendly_mastery_link && (
                      <a href={settings.calendly_mastery_link} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-3 p-4 bg-white border border-gray-100 hover:border-rtb-purple/30 hover:shadow-md transition-all rounded-xl">
                        <Calendar className="w-5 h-5 text-rtb-purple" />
                        <span className="text-gray-700">Book Your First Mastery Session (Wed/Sat)</span>
                        <ExternalLink className="w-4 h-4 text-gray-300 ml-auto" />
                      </a>
                    )}
                  </div>
                  <Button onClick={handleCompleteOnboarding} className="btn-primary text-lg px-10 py-6" data-testid="start-program-btn">
                    Start My 20-Day Journey <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              )}

              {user?.onboarding_step >= 4 && (
                <div className="text-center py-12">
                  <CheckCircle2 className="w-12 h-12 text-rtb-green mx-auto mb-4" />
                  <p className="text-gray-500">Onboarding complete! Switch to the Program tab to continue your journey.</p>
                </div>
              )}
            </motion.div>
          )}

          {/* PROGRAM TAB */}
          {activeTab === 'program' && (
            <motion.div key="program" initial="hidden" animate="visible" exit="hidden" variants={fadeIn}>
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl font-oswald font-bold text-gray-900">Your 20-Day Program</h2>
                  <p className="text-gray-500 text-sm">Phase: <span className={`font-bold capitalize ${PHASE_COLORS[user?.program_phase] || ''}`}>{user?.program_phase?.replace('_', ' ')}</span></p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-oswald font-bold text-rtb-purple">{completedDays.length}/20</p>
                  <p className="text-gray-400 text-xs">Days Completed</p>
                </div>
              </div>

              {/* Progress bar */}
              <div className="w-full h-3 bg-gray-100 rounded-full mb-6 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${(completedDays.length / 20) * 100}%` }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className="h-full bg-gradient-to-r from-rtb-purple to-rtb-green rounded-full"
                />
              </div>

              {/* Gamification: Milestones */}
              <div className="mb-8" data-testid="milestones-section">
                <div className="flex items-center gap-2 mb-4">
                  <Trophy className="w-5 h-5 text-amber-500" />
                  <h3 className="font-oswald font-bold text-gray-900 text-lg">Milestones</h3>
                  <span className="ml-auto text-xs bg-rtb-purple/10 text-rtb-purple px-2.5 py-1 rounded-full font-bold">
                    {MILESTONES.filter(m => completedDays.length >= m.day).length}/{MILESTONES.length} Unlocked
                  </span>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                  {MILESTONES.map(m => {
                    const unlocked = completedDays.length >= m.day;
                    const MIcon = m.icon;
                    return (
                      <motion.div
                        key={m.day}
                        whileHover={{ scale: unlocked ? 1.05 : 1 }}
                        className={`relative flex flex-col items-center p-3 border rounded-xl text-center transition-all ${
                          unlocked
                            ? `${m.bg} border-transparent shadow-sm`
                            : 'bg-gray-50 border-gray-100 opacity-40 grayscale'
                        }`}
                        data-testid={`milestone-${m.day}`}
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                          unlocked ? 'bg-white shadow-sm' : 'bg-gray-100'
                        }`}>
                          <MIcon className={`w-5 h-5 ${unlocked ? m.color : 'text-gray-300'}`} />
                        </div>
                        <p className={`text-[10px] font-bold uppercase tracking-wide ${
                          unlocked ? m.color : 'text-gray-400'
                        }`}>{m.label}</p>
                        <p className="text-[9px] text-gray-400 mt-0.5">Day {m.day}</p>
                        {unlocked && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-rtb-green rounded-full flex items-center justify-center"
                          >
                            <CheckCircle2 className="w-3 h-3 text-white" />
                          </motion.div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>

                {/* Streak & XP bar */}
                <div className="flex gap-3 mt-4">
                  <div className="flex-1 flex items-center gap-3 p-3 bg-white border border-gray-100 rounded-xl" data-testid="xp-counter">
                    <div className="w-9 h-9 bg-rtb-purple/10 rounded-lg flex items-center justify-center">
                      <Zap className="w-5 h-5 text-rtb-purple" />
                    </div>
                    <div>
                      <p className="font-oswald font-bold text-gray-900 text-lg">{completedDays.length * 50} XP</p>
                      <p className="text-gray-400 text-[10px]">50 XP per completed day</p>
                    </div>
                  </div>
                  <div className="flex-1 flex items-center gap-3 p-3 bg-white border border-gray-100 rounded-xl" data-testid="streak-counter">
                    <div className="w-9 h-9 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Flame className="w-5 h-5 text-orange-500" />
                    </div>
                    <div>
                      <p className="font-oswald font-bold text-gray-900 text-lg">
                        {(() => {
                          let streak = 0;
                          for (let i = completedDays.length; i >= 1; i--) {
                            if (completedDays.includes(i)) streak++;
                            else break;
                          }
                          return streak;
                        })()}-Day Streak
                      </p>
                      <p className="text-gray-400 text-[10px]">Keep it going!</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Background Check Status Banner */}
              {bgCheck && bgCheck.status !== 'not_started' && (
                <div className={`p-4 border rounded-xl mb-6 flex items-center gap-3 ${
                  bgCheck.status === 'clear' ? 'bg-emerald-50 border-emerald-200' :
                  bgCheck.status === 'review_needed' ? 'bg-amber-50 border-amber-200' :
                  bgCheck.status === 'invited' ? 'bg-blue-50 border-blue-200' :
                  'bg-gray-50 border-gray-200'
                }`} data-testid="bg-check-banner">
                  {bgCheck.status === 'clear' && <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />}
                  {bgCheck.status === 'review_needed' && <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />}
                  {bgCheck.status === 'invited' && <Clock className="w-5 h-5 text-blue-500 flex-shrink-0" />}
                  {bgCheck.status === 'processing' && <Loader2 className="w-5 h-5 text-rtb-purple animate-spin flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${
                      bgCheck.status === 'clear' ? 'text-emerald-700' :
                      bgCheck.status === 'review_needed' ? 'text-amber-700' :
                      bgCheck.status === 'invited' ? 'text-blue-700' :
                      'text-gray-700'
                    }`}>
                      {bgCheck.status === 'clear' && 'Background Check Cleared'}
                      {bgCheck.status === 'review_needed' && 'Background Check Under Review'}
                      {bgCheck.status === 'invited' && 'Background Check: Action Needed'}
                      {bgCheck.status === 'processing' && 'Background Check Processing...'}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {bgCheck.status === 'invited' && 'Check your email for the Checkr invitation link to complete your background check.'}
                      {bgCheck.status === 'processing' && 'Your background check is being processed. This usually takes 1-3 business days.'}
                      {bgCheck.status === 'clear' && 'Your background check has been completed successfully.'}
                      {bgCheck.status === 'review_needed' && 'Our team will review your results and contact you shortly.'}
                    </p>
                  </div>
                  {bgCheck.status === 'invited' && bgCheck.invitation_url && (
                    <a href={bgCheck.invitation_url} target="_blank" rel="noopener noreferrer">
                      <Button size="sm" className="bg-rtb-purple text-white rounded-full text-xs px-4 hover:bg-rtb-purple/90 flex-shrink-0" data-testid="complete-bg-check-btn">
                        Complete Now
                      </Button>
                    </a>
                  )}
                </div>
              )}

              {/* Mastery Session */}
              {settings.calendly_mastery_link && (
                <div className="p-4 bg-white border border-gray-100 rounded-xl mb-8 flex items-center justify-between shadow-sm">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-rtb-purple" />
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Mastery Sessions</p>
                      <p className="text-gray-400 text-xs">Wednesday & Saturday - 40 min</p>
                    </div>
                  </div>
                  <a href={settings.calendly_mastery_link} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" className="bg-rtb-purple text-white rounded-full text-xs px-4 hover:bg-rtb-purple/90">Book Session</Button>
                  </a>
                </div>
              )}

              {/* Day cards */}
              <div className="space-y-2">
                {PROGRAM_DAYS.map((d) => {
                  const completed = completedDays.includes(d.day);
                  const isNext = !completed && d.day === (user?.program_day || 1);
                  const PhaseIcon = PHASE_ICONS[d.phase] || Target;
                  return (
                    <motion.div
                      key={d.day}
                      ref={isNext ? currentDayRef : undefined}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: d.day * 0.02 }}
                      className={`p-4 border transition-all rounded-xl ${
                        completed ? 'bg-rtb-green/5 border-rtb-green/20' :
                        isNext ? 'bg-white border-rtb-purple/30 shadow-md shadow-rtb-purple/5' :
                        'bg-white border-gray-100'
                      }`}
                      data-testid={`day-${d.day}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-xl ${
                          completed ? 'bg-rtb-green text-white' : PHASE_BG[d.phase] || 'bg-gray-100'
                        }`}>
                          {completed ? <CheckCircle2 className="w-5 h-5" /> :
                            <span className={`font-oswald font-bold text-sm ${PHASE_COLORS[d.phase]}`}>{d.day}</span>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <PhaseIcon className={`w-3.5 h-3.5 ${PHASE_COLORS[d.phase]}`} />
                            <span className={`text-[10px] uppercase tracking-wider font-bold ${PHASE_COLORS[d.phase]}`}>{d.phase}</span>
                          </div>
                          <p className="font-medium text-gray-900">{d.title}</p>
                          <p className="text-gray-400 text-sm">{d.desc}</p>
                        </div>
                        {isNext && (
                          <Button onClick={() => handleCompleteDay(d.day)} size="sm" className="bg-rtb-purple text-white rounded-full text-xs px-4 hover:bg-rtb-purple/90 flex-shrink-0" data-testid={`complete-day-${d.day}`}>
                            Complete
                          </Button>
                        )}
                        {completed && <CheckCircle2 className="w-5 h-5 text-rtb-green flex-shrink-0" />}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* MEETINGS TAB */}
          {activeTab === 'meetings' && (
            <motion.div key="meetings" initial="hidden" animate="visible" exit="hidden" variants={fadeIn}>
              <h2 className="text-2xl font-oswald font-bold text-gray-900 mb-2">Meetings</h2>
              <p className="text-gray-500 text-sm mb-6">Join upcoming sessions and mastery meetings.</p>
              <MeetingsTab settings={settings} />
            </motion.div>
          )}

          {/* LLC TAB */}
          {activeTab === 'llc' && (
            <motion.div key="llc" initial="hidden" animate="visible" exit="hidden" variants={fadeIn}>
              <h2 className="text-2xl font-oswald font-bold text-gray-900 mb-2">LLC Formation</h2>
              <p className="text-gray-500 text-sm mb-8">Step-by-step guidance for forming your LLC, plus AI-generated hardship letters.</p>
              <div className="flex items-center gap-3 mb-6 p-4 bg-white border border-gray-100 rounded-xl">
                <Shield className="w-5 h-5 text-rtb-purple" />
                <span className="text-sm text-gray-700">Status: <span className="text-rtb-purple font-bold capitalize">{user?.llc_status?.replace('_', ' ')}</span></span>
                {user?.llc_state && <span className="text-gray-400 text-sm">| State: {user?.llc_state}</span>}
              </div>
              <Link to="/portal/llc" data-testid="start-llc-btn">
                <Button className="btn-primary">
                  {user?.llc_status === 'not_started' ? 'Start LLC Formation' : 'Continue LLC Formation'} <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
          )}

          {/* RESOURCES TAB */}
          {activeTab === 'resources' && (
            <motion.div key="resources" initial="hidden" animate="visible" exit="hidden" variants={fadeIn}>
              <h2 className="text-2xl font-oswald font-bold text-gray-900 mb-6">Resources</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <motion.a whileHover={{ y: -4 }} href="https://www.youtube.com/watch?v=Z4gwVtYD3zA&t=49s" target="_blank" rel="noopener noreferrer"
                  className="p-6 bg-white border border-gray-100 hover:border-rtb-purple/30 hover:shadow-lg transition-all rounded-xl group">
                  <BookOpen className="w-8 h-8 text-rtb-purple mb-3" />
                  <h3 className="font-oswald font-bold text-lg text-gray-900 mb-1">Financial Literacy Training</h3>
                  <p className="text-gray-400 text-sm">Tax optimization and financial strategies for entrepreneurs.</p>
                </motion.a>
                <motion.a whileHover={{ y: -4 }} href="#"
                  className="p-6 bg-white border border-gray-100 hover:border-rtb-purple/30 hover:shadow-lg transition-all rounded-xl group">
                  <FileText className="w-8 h-8 text-rtb-purple mb-3" />
                  <h3 className="font-oswald font-bold text-lg text-gray-900 mb-1">Business Ebook</h3>
                  <p className="text-gray-400 text-sm">Your comprehensive guide to building a successful business.</p>
                </motion.a>
                {settings.calendly_intro_link && (
                  <motion.a whileHover={{ y: -4 }} href={settings.calendly_intro_link} target="_blank" rel="noopener noreferrer"
                    className="p-6 bg-white border border-gray-100 hover:border-rtb-purple/30 hover:shadow-lg transition-all rounded-xl group">
                    <Calendar className="w-8 h-8 text-rtb-purple mb-3" />
                    <h3 className="font-oswald font-bold text-lg text-gray-900 mb-1">Book Introduction Call</h3>
                    <p className="text-gray-400 text-sm">30-minute free introduction call with our team.</p>
                  </motion.a>
                )}
                {settings.calendly_mastery_link && (
                  <motion.a whileHover={{ y: -4 }} href={settings.calendly_mastery_link} target="_blank" rel="noopener noreferrer"
                    className="p-6 bg-white border border-gray-100 hover:border-rtb-purple/30 hover:shadow-lg transition-all rounded-xl group">
                    <Calendar className="w-8 h-8 text-rtb-green mb-3" />
                    <h3 className="font-oswald font-bold text-lg text-gray-900 mb-1">Mastery Session</h3>
                    <p className="text-gray-400 text-sm">40-minute mastery session, Wednesday & Saturday.</p>
                  </motion.a>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <LegalFooter />
    </div>
  );
}
